import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { useSiteSettings } from '../services/siteSettingsService';

const contactPillars = [
  'Reponse strategique sous 24h ouvrables',
  'Echange centre sur le ROI et les priorites',
  'Recommandations concretes, sans jargon inutile'
];

const conversationOutcomes = [
  {
    title: 'Clarifier la bonne priorite',
    description: 'Nous cadrons ce qu il faut lancer maintenant, ce qui peut attendre, et ce qui doit etre automatise.'
  },
  {
    title: 'Poser une feuille de route premium',
    description: 'Vous repartez avec une lecture plus nette du budget, du niveau d execution et du bon tempo.'
  },
  {
    title: 'Choisir la meilleure architecture',
    description: 'Site, CRM, IA, acquisition, automatisation ou plateforme sur mesure: nous orientons vers la bonne pile.'
  }
];

const Contact = () => {
  const { settings } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const contactChannels = [
    {
      icon: 'Strategie',
      title: 'Premier cadrage',
      value: 'Contact initial',
      description:
        'Pour un nouveau projet, une refonte premium ou une mission d accompagnement.',
      href: `mailto:${settings.contactEmail}`,
    },
    {
      icon: 'Email',
      title: 'Email direct',
      value: settings.supportEmail,
      description:
        'Ideal pour envoyer un besoin detaille, un brief, un devis existant ou des acces.',
      href: `mailto:${settings.supportEmail}`,
    },
    {
      icon: 'Telephone',
      title: 'Ligne directe',
      value: settings.phoneDisplay,
      description:
        'Pour accelerer une decision, debloquer un sujet urgent ou organiser un echange rapide.',
      href: settings.phoneHref,
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!settings.contactWebhookUrl.trim()) {
        throw new Error('Aucun webhook de contact n est configuré dans les paramètres');
      }

      const response = await fetch(settings.contactWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Message envoye avec succes !');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l envoi');
      }
    } catch (error) {
      alert('Une erreur est survenue. Veuillez reessayer.');
      console.error('Error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact - {settings.brandFullName}</title>
        <meta
          name="description"
          content={`Parlons de votre projet avec ${settings.brandFullName}. Cadrage, strategie, design, developpement, IA et execution premium.`}
        />
      </Helmet>

      <main>
        <section className="py-14 md:py-24 bg-gradient-to-b from-gray-900 via-black to-gray-950 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,106,0,0.24),_transparent_42%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_30%)]" />
            <motion.div
              className="absolute -top-10 right-10 h-72 w-72 rounded-full bg-neon-orange/10 blur-3xl"
              animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.45, 0.2] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur"
                >
                  <span className="h-2 w-2 rounded-full bg-neon-orange" />
                  Echange premium, simple et concret
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  className="mt-6 max-w-4xl text-4xl font-bold leading-tight text-white md:text-6xl"
                >
                  Parlons de votre prochain
                  <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> palier de croissance</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.16 }}
                  className="mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
                >
                  Vous avez un projet web, une refonte, un besoin IA, une automatisation, un enjeu d acquisition
                  ou une execution haut de gamme a structurer ? Nous construisons des solutions claires, ambitieuses
                  et rentables, sans surcharger le process.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.24 }}
                  className="mt-8 grid gap-3 sm:grid-cols-3"
                >
                  {contactPillars.map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-gray-200 backdrop-blur"
                    >
                      {item}
                    </div>
                  ))}
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.28 }}
                className="rounded-[28px] border border-white/10 bg-white/6 p-6 shadow-2xl shadow-black/30 backdrop-blur"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm uppercase tracking-[0.24em] text-neon-orange">Avant notre echange</p>
                    <h2 className="mt-2 text-2xl font-semibold text-white">Ce que nous preparons pour vous</h2>
                  </div>
                  <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-gray-200">
                    {settings.brandShortName}
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  {conversationOutcomes.map((item, index) => (
                    <div
                      key={item.title}
                      className="rounded-2xl border border-white/8 bg-black/20 p-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neon-orange text-sm font-semibold text-white">
                          {index + 1}
                        </div>
                        <h3 className="text-base font-semibold text-white">{item.title}</h3>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-gray-300">{item.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-gradient-to-b from-[#f8f6f2] via-white to-white relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,106,0,0.08),_transparent_35%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid gap-8 xl:grid-cols-[1.05fr_0.95fr]">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="rounded-[30px] border border-gray-200 bg-white p-6 shadow-[0_24px_80px_rgba(17,24,39,0.08)] md:p-8"
              >
                <div className="flex flex-col gap-3 border-b border-gray-100 pb-6">
                  <p className="text-sm uppercase tracking-[0.24em] text-neon-orange">Brief projet</p>
                  <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Dites-nous ou vous voulez aller</h2>
                  <p className="max-w-2xl text-base leading-7 text-gray-600">
                    Plus votre message est clair, plus notre premiere reponse sera utile. Si vous avez deja un budget,
                    une echeance, un site existant ou un besoin IA precis, indiquez-le simplement.
                  </p>
                </div>

                <div className="mt-8 space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Vos informations</h3>
                    <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Nom et prenom</span>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Votre nom complet"
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Email</span>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="nom@entreprise.com"
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Telephone</span>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+41 ..."
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Entreprise</span>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Nom de votre entreprise"
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Votre demande</h3>
                    <div className="mt-5 space-y-5">
                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Objet</span>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Refonte premium, IA, acquisition, CRM, automatisation..."
                          className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-sm font-medium text-gray-700">Message</span>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={7}
                          placeholder="Expliquez le contexte, vos objectifs, les enjeux, l echeance, le budget estime ou ce qui bloque aujourd hui."
                          className="w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base outline-none transition-all duration-300 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/15"
                          required
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-4 border-t border-gray-100 pt-6 md:flex-row md:items-center md:justify-between">
                  <p className="max-w-xl text-sm leading-6 text-gray-500">
                    En nous contactant, vous nous donnez les elements utiles pour revenir avec une reponse plus juste,
                    plus rapide et mieux calibree.
                  </p>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
                  >
                    Envoyer votre message
                  </motion.button>
                </div>
              </motion.form>

              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-[0_24px_80px_rgba(17,24,39,0.06)]"
                >
                  <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Ce qui fait la difference</p>
                  <h2 className="mt-3 text-2xl font-bold text-gray-900">Une approche premium, sans theatrale inutile</h2>
                  <div className="mt-6 space-y-4">
                    {contactChannels.map((item) => (
                      <a
                        key={item.title}
                        href={item.href}
                        className="block rounded-2xl border border-gray-100 bg-[#f8f6f2] p-5 transition-all duration-300 hover:border-neon-orange/30 hover:shadow-lg hover:shadow-neon-orange/5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-neon-orange">{item.icon}</p>
                            <h3 className="mt-2 text-lg font-semibold text-gray-900">{item.title}</h3>
                            <p className="mt-1 text-base font-medium text-gray-800">{item.value}</p>
                            <p className="mt-3 text-sm leading-6 text-gray-600">{item.description}</p>
                          </div>
                          <span className="mt-1 text-neon-orange">↗</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[28px] bg-gray-950 p-6 text-white shadow-2xl shadow-black/20"
                >
                  <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Quand nous contacter</p>
                  <div className="mt-5 grid gap-4">
                    {[
                      'Vous sentez que votre positionnement ou votre execution peut monter d un cran.',
                      'Votre site, votre CRM ou votre stack IA n accompagne plus la croissance.',
                      'Vous voulez vendre plus cher avec une perception plus forte et une structure plus solide.'
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                        <p className="text-sm leading-6 text-gray-200">{item}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;
